import type { ControlMap } from '../control/ControlMap';
import { clampWalk } from '../game/ground';

// Deterministic belt-scroll motion. Replaces the platformer's Arcade-Physics
// step, which resolved screen-space AABBs — the wrong space once "up" means
// depth rather than height.
//
// Three axes, integrated by hand, no simulation solver:
//   x  along the street  — direct velocity, no accel ramp (crisp, reproducible).
//   z  into the street   — direct velocity, slower than x. Beat-'em-ups always
//                          walk the depth axis slower; it reads as foreshortening
//                          and it stops the street feeling like a trampoline.
//   h  above the ground  — the only axis with gravity. Jump lifts h; landing is
//                          h reaching 0. There are no platforms to land on, so
//                          there is no coyote time and no jump-through logic.
//
// Nothing here knows about render scale. An actor moves the same number of
// ground pixels per second at the back of the street as at the front, which is
// what keeps a walk from feeling like it drags when you step upstage.

/** Tuning constants — the whole feel of the slice lives here, in one place, in
 *  world pixels and seconds. */
export const MOVE = {
  /** Along-street run speed, px/s. */
  runSpeed: 300,
  /** Into-street walk speed, px/s along the oblique axis. Deliberately slower:
   *  at 300 the actor crosses the street's whole depth in a third of a second. */
  depthSpeed: 132,
  /** Constant downward acceleration on `h`, px/s². */
  gravity: 2900,
  /** Jump take-off velocity on `h`, px/s. Peak ≈ v²/2g ≈ 129px above the ground. */
  jumpSpeed: 865,
  /** Velocity kept when the jump is released early, for variable jump height. */
  jumpCutMultiplier: 0.35,
  /** Window before landing during which a jump press is remembered, sec. */
  jumpBuffer: 0.1,
  /** Terminal fall speed clamp on `h`, px/s. */
  maxFallSpeed: 1400,
} as const;

/** An actor's position on the belt, and the velocities that move it. */
export interface BeltState {
  x: number;
  z: number;
  h: number;
  /** Vertical velocity on `h`. Positive is upward. */
  vh: number;
  grounded: boolean;
  jumping: boolean;
  /** Seconds of buffered jump remaining. */
  buffer: number;
}

export function newBeltState(x: number, z: number): BeltState {
  return { x, z, h: 0, vh: 0, grounded: true, jumping: false, buffer: 0 };
}

export interface BeltStep {
  /** True on the frame a jump actually fired. */
  jumped: boolean;
  grounded: boolean;
  /** Signed along-street intent (-1/0/1) — drives facing. */
  moveDir: number;
  /** Signed into-street intent (-1/0/1). Never drives facing: an actor walking
   *  upstage keeps facing the way he was, exactly as in TMNT. */
  depthDir: number;
  /** True when the actor is travelling on either ground axis. */
  moving: boolean;
}

/**
 * Advance one belt step. Mutates `s`.
 *
 * Airborne actors keep full control of x and z, which is the genre convention —
 * a jump is a way to cross the street, not a commitment.
 */
export function stepBelt(s: BeltState, ctrl: ControlMap, dtSec: number): BeltStep {
  const moveDir = (ctrl.get('right').down ? 1 : 0) - (ctrl.get('left').down ? 1 : 0);
  const depthDir = (ctrl.get('down').down ? 1 : 0) - (ctrl.get('up').down ? 1 : 0);
  // `up` walks INTO the street, which is -z. `down` walks toward the camera, +z.
  const dz = -depthDir;

  // Clamped against the walkable ground: the belt rectangle, or the location's
  // traced polygon when it carries one (Order 16.6). The step is resolved as a
  // move from the current position, so a wall stops you but lets you slide.
  const step = clampWalk(s.x, s.z, s.x + moveDir * MOVE.runSpeed * dtSec, s.z + dz * MOVE.depthSpeed * dtSec);
  s.x = step.x;
  s.z = step.z;

  // ── Jump on h ────────────────────────────────────────────────────────────
  s.buffer = ctrl.get('jump').pressed ? MOVE.jumpBuffer : Math.max(0, s.buffer - dtSec);

  let jumped = false;
  if (s.buffer > 0 && s.grounded) {
    s.vh = MOVE.jumpSpeed;
    s.grounded = false;
    s.jumping = true;
    s.buffer = 0;
    jumped = true;
  }

  // Variable height: releasing early cuts the remaining rise.
  if (s.jumping && s.vh > 0 && ctrl.get('jump').released) {
    s.vh *= MOVE.jumpCutMultiplier;
    s.jumping = false;
  }

  if (!s.grounded) {
    s.vh -= MOVE.gravity * dtSec;
    if (s.vh < -MOVE.maxFallSpeed) s.vh = -MOVE.maxFallSpeed;
    s.h += s.vh * dtSec;
    if (s.h <= 0) {
      s.h = 0;
      s.vh = 0;
      s.grounded = true;
      s.jumping = false;
    }
  }

  return {
    jumped,
    grounded: s.grounded,
    moveDir,
    depthDir,
    moving: moveDir !== 0 || depthDir !== 0,
  };
}
